import { useState, useCallback, type ReactNode, type ElementType } from 'react'
import { ChevronRight } from 'lucide-react'

const STORAGE_KEY = 'inbox-sidebar-sections'

function readPersistedState(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
  } catch {
    return {}
  }
}

function persistSectionState(id: string, open: boolean) {
  const state = readPersistedState()
  state[id] = open
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
}

interface CollapsibleSectionProps {
  title: string
  icon?: ElementType
  defaultOpen?: boolean
  summary?: string
  count?: number
  children: ReactNode
}

export default function CollapsibleSection({
  title,
  icon: Icon,
  defaultOpen = false,
  summary,
  count,
  children,
}: CollapsibleSectionProps) {
  const sectionId = title.toLowerCase().replace(/\s+/g, '-')

  const [isOpen, setIsOpen] = useState(() => {
    const persisted = readPersistedState()
    return sectionId in persisted ? persisted[sectionId] : defaultOpen
  })

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev
      persistSectionState(sectionId, next)
      return next
    })
  }, [sectionId])

  return (
    <div className="border-b border-gray-200">
      <button
        onClick={toggle}
        className="w-full flex items-center gap-1.5 p-4 pb-3 cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <ChevronRight
          className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform duration-150 ${
            isOpen ? 'rotate-90' : ''
          }`}
        />
        {Icon && <Icon className="w-3.5 h-3.5 text-gray-500 shrink-0" />}
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          {title}
        </h4>
        {count !== undefined && (
          <span className="bg-orange-100 text-orange-700 text-xs font-medium px-1.5 py-0.5 rounded-full ml-auto">
            {count}
          </span>
        )}
        {!isOpen && summary && !count && (
          <span className="text-xs text-gray-400 ml-auto truncate max-w-[140px]">
            {summary}
          </span>
        )}
      </button>
      {isOpen && <div className="px-4 pb-4">{children}</div>}
    </div>
  )
}
